import * as THREE from "three";
import { Tank } from "./Tank";
import { createEnvironment, Environment, isInMud } from "./environment";
import { setupSocket } from "./network";
import { CAMERA_CONFIG, ENVIRONMENT_CONFIG } from "./config";
import { createHUD, updateHUD, removeHUD, setCrosshairVisibility, showWastedOverlay, hideWastedOverlay } from "./hud";
import { EnvironmentService } from "./services/EnvironmentService";
import { BotManager } from "./BotManager";
import { Bot } from "./Bot";
import { TrainingTrackManager } from "./training/ModularTrainingTrackManager";
import { TrainingTrackService } from "./training/TrainingTrackService";
import { TrainingTrackSetup } from "./training/TrainingTrackSetup";
import { EventManager } from "./EventManager";
import { GraphicsSettings, GraphicsSettingsManager } from "./config/graphics";
import { MobileController } from "./controllers/MobileController";

let scene: THREE.Scene;
let camera: THREE.PerspectiveCamera;
let renderer: THREE.WebGLRenderer;
let tank: Tank;
let environment: Environment;
let raycaster: THREE.Raycaster; //it is used in initGame
let groundPlane: THREE.Plane; //it is also used in initGame
let groundNormal: THREE.Vector3;
let lastFrameTime = 0;
let lastFpsUpdateTime = 0;
let fps = 0;
let frameCount = 0;
let isMouseLocked = false;
const MIN_CAMERA_DISTANCE = CAMERA_CONFIG.MIN_DISTANCE;
const MAX_CAMERA_DISTANCE = CAMERA_CONFIG.MAX_DISTANCE;
let cameraDistance = CAMERA_CONFIG.DEFAULT_DISTANCE;
const zoomSpeed = CAMERA_CONFIG.ZOOM_SPEED; //it is also used!
let trainingTrackManager: TrainingTrackManager;
let trainingTrackService: TrainingTrackService;

declare global {
  interface Window {
    analyzeScene: () => any;
  }
}

declare global {
  interface Window {
    inspectSceneGeometry: () => any[];
  }
}

// Deklaracja dla TypeScript, aby crosshairPosition było dostępne globalnie
declare global {
  interface Window {
    socket?: any;
    playerTank?: Tank;
    gameState?: {
      tanks: Tank[];
      scene?: THREE.Scene;
      environmentService?: {
        getRandomRespawnPoint: () => {
          position: THREE.Vector3;
          rotation: number;
        } | null;
      };
      trainingTrackManager?: TrainingTrackManager;
      trainingTrackService?: TrainingTrackService;
      Bot?: typeof Bot;
      botManager?: BotManager;
    };
    tanks?: Tank[];
    remoteTanks?: Tank[];
    showDamageIndicator?: (damage: number) => void;
    crosshairPosition: THREE.Vector3;
    updateHUDHealth?: (health: number) => void;
    cameraDistance?: number;
    setCameraDistance?: (distance: number) => void;
  }
}

// Inicjalizacja zmiennych globalnych
window.crosshairPosition = new THREE.Vector3();

// Expose camera distance and setter function globally
window.cameraDistance = cameraDistance;
window.setCameraDistance = (distance: number) => {
  cameraDistance = distance;
  window.cameraDistance = distance;
};

let botManager: BotManager = new BotManager();

// Dodaj zmienną do śledzenia ruchu czołgu
let isMoving = false;
let lastLogTime = 0;
const LOG_THROTTLE_MS = 10000; // Loguj co 10s podczas ruchu

const isLocalDevEnvironment = window.location.hostname === "localhost";

// Dodaj zmienne do śledzenia czasu ładowania
let gameStartTime = 0;
let tankVisibleTime = 0;
let environmentLoadedTime = 0;
let lastChunkUpdateTime = 0;
let lastAppliedCameraFov = 75;

const cameraDirection = new THREE.Vector3();
const cameraDirectionXZ = new THREE.Vector3();
const aimDirection = new THREE.Vector3();
const tankForward = new THREE.Vector3();
const horizontalAimDirection = new THREE.Vector3();
const tankDirection = new THREE.Vector3();
const cameraOffset = new THREE.Vector3();
const targetCameraPos = new THREE.Vector3();
const lookAtPoint = new THREE.Vector3();
const botUpdateTanks: Tank[] = [];

let graphicsSettingsListener: EventListener | null = null;
const MAX_RENDER_PIXEL_RATIO = 2;
const LOW_QUALITY_PIXEL_RATIO_SCALE = 0.75;

// Perf: shadow camera follows the player tank instead of covering the whole
// 500x500m map. A tight frustum gives ~3x sharper shadows for the same map
// size AND a much smaller shadow pass. Same visuals near the player, better
// texel density (i.e. quality goes up, not down).
const SHADOW_FOLLOW_OFFSET = new THREE.Vector3(60, 90, 60);
const SHADOW_FRUSTUM_HALF_EXTENT = 90;
let shadowLight: THREE.DirectionalLight | null = null;
const _shadowTargetPos = new THREE.Vector3();

function updateShadowFollow(): void {
  if (!shadowLight || !tank) {
    return;
  }
  _shadowTargetPos.copy(tank.mesh.position);
  shadowLight.position.copy(_shadowTargetPos).add(SHADOW_FOLLOW_OFFSET);
  shadowLight.target.position.copy(_shadowTargetPos);
  shadowLight.target.updateMatrixWorld();
}

function getClampedRendererPixelRatio(quality: GraphicsSettings["quality"]): number {
  const qualityScale = quality === "low" ? LOW_QUALITY_PIXEL_RATIO_SCALE : 1;
  return Math.min(window.devicePixelRatio * qualityScale, MAX_RENDER_PIXEL_RATIO);
}

// Perf: PCFSoft is ~2x the shadow filtering cost of PCF. Visual difference on
// hard-surface tanks/rocks is negligible - reserve PCFSoft for high preset.
function getShadowMapType(quality: GraphicsSettings["quality"]): THREE.ShadowMapType {
  return quality === "high" ? THREE.PCFSoftShadowMap : THREE.PCFShadowMap;
}

// Perf: these never contribute meaningful shadows, only shadow-pass draw calls.
// NOTE: papercraft trees use a FrontSide ShaderMaterial and MUST keep casting.
function shouldSkipShadowCasting(object: THREE.Object3D): boolean {
  if (
    object instanceof THREE.Sprite ||
    object instanceof THREE.Line ||
    object instanceof THREE.Points ||
    object instanceof THREE.InstancedMesh
  ) {
    // Grass (InstancedMesh), labels, trails - no shadow value.
    return true;
  }
  if (object instanceof THREE.Mesh) {
    const material = object.material as THREE.Material | THREE.Material[];
    const materials = Array.isArray(material) ? material : [material];
    for (const mat of materials) {
      // Transparent billboards/clouds/glow/respawn-zone (MeshBasicMaterial).
      if (mat instanceof THREE.MeshBasicMaterial && mat.transparent) {
        return true;
      }
      // Sky dome gradient (BackSide shader sphere).
      if (mat instanceof THREE.ShaderMaterial && mat.side === THREE.BackSide) {
        return true;
      }
    }
  }
  return false;
}

function logDev(...args: unknown[]): void {
  if (isLocalDevEnvironment) {
    console.log(...args);
  }
}

// Initialize global game state
window.gameState = {
  tanks: [],
  Bot: Bot,
  botManager: botManager,
};

// Zmień definicję memoryStats
/*
const memoryStats = {
  startTime: Date.now(),
  runtime: 0,
  geometries: 0,
  textures: 0,
  objects: 0,
  triangles: 0,
  calls: 0,
  frames: 0
};
*/

// Funkcja do logowania z ograniczeniem częstotliwości
function throttledLog(message: string, data: any, force = false) {
  const now = Date.now();

  // Loguj tylko jeśli czołg się porusza lub minęło wystarczająco dużo czasu od ostatniego logu
  // lub jeśli zostało wymuszone logowanie (np. podczas strzelania)
  if (force || (isMoving && now - lastLogTime > LOG_THROTTLE_MS)) {
    console.log(message, data);
    lastLogTime = now;
  }
}

// Funkcja do sprawdzania, czy czołg się porusza
function checkTankMovement() {
  // Czołg porusza się, jeśli jego prędkość jest większa niż 0.01
  isMoving = Math.abs(tank.movementSystem.currentSpeed) > 0.01;
}

export function initGame(
  canvas: HTMLCanvasElement,
  options?: {
    shadowsEnabled?: boolean;
    selectedTankModel?: string;
    username?: string;
    isMobile?: boolean;
  },
): Promise<void> {
  gameStartTime = performance.now();
  console.log("[Game] Initializing game with options:", options);

  // Get initial graphics settings
  const graphicsManager = GraphicsSettingsManager.getInstance();
  const initialSettings = graphicsManager.getCurrentSettings();
  console.log("[Game] Initial graphics settings:", initialSettings);

  // Subscribe to graphics settings changes (single listener)
  if (graphicsSettingsListener) {
    document.removeEventListener("graphics:settingsChanged", graphicsSettingsListener);
  }

  const handleGraphicsSettingsChanged = ((event: CustomEvent<{ settings: GraphicsSettings }>) => {
    const settings = event.detail.settings;
    logDev("[Graphics Debug] Received settings event:", {
      settings,
      hasRenderer: !!renderer,
      hasScene: !!scene,
    });
    if (renderer) {
      // Update shadow settings
      logDev("[Shadow Debug] Updating shadow settings:", {
        shadowsEnabled: settings.shadowsEnabled,
        shadowMapSize: settings.shadowMapSize,
        quality: settings.quality,
      });
      renderer.shadowMap.enabled = settings.shadowsEnabled;
      renderer.shadowMap.type = getShadowMapType(settings.quality);
      renderer.setPixelRatio(getClampedRendererPixelRatio(settings.quality));

      // Update directional light shadow settings
      const directionalLight = scene.children.find(
        (child) => child instanceof THREE.DirectionalLight,
      ) as THREE.DirectionalLight;
      if (directionalLight) {
        logDev("[Shadow Debug] Directional light found, updating shadow properties:", {
          castShadow: settings.shadowsEnabled,
          mapSize: settings.shadowMapSize,
        });
        directionalLight.castShadow = settings.shadowsEnabled;
        if (directionalLight.shadow) {
          directionalLight.shadow.mapSize.width = settings.shadowMapSize;
          directionalLight.shadow.mapSize.height = settings.shadowMapSize;
        } else {
          console.warn("[Shadow Debug] Directional light shadow property is null");
        }
      } else {
        console.warn("[Shadow Debug] No directional light found in scene");
      }

      // Update shadow properties for all objects in the scene.
      // Perf: grass/sprites/clouds/sky add shadow-pass draws with no visual
      // benefit - they never cast.
      let meshCount = 0;
      let skippedCount = 0;
      scene.traverse((object) => {
        if (object instanceof THREE.Mesh) {
          if (shouldSkipShadowCasting(object)) {
            object.castShadow = false;
            skippedCount++;
            return;
          }
          meshCount++;
          object.castShadow = settings.shadowsEnabled;
          object.receiveShadow = settings.shadowsEnabled;
        }
      });
      logDev(`[Shadow Debug] Updated shadow properties for ${meshCount} meshes (skipped ${skippedCount})`);
    }
    logDev("Graphics settings updated:", settings);
  }) as EventListener;

  document.addEventListener("graphics:settingsChanged", handleGraphicsSettingsChanged);
  graphicsSettingsListener = handleGraphicsSettingsChanged;

  // Inicjalizacja podstawowych komponentów Three.js
  scene = new THREE.Scene();
  // Usuwamy stare ustawienia tła i mgły, ponieważ są teraz zarządzane przez SkyBox
  // scene.background = new THREE.Color(0x87ceeb);
  // scene.fog = new THREE.Fog(0x87ceeb, 20, 100);

  camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
  camera.position.set(0, 5, -10);

  // Store camera in scene.userData for raycasting
  scene.userData.camera = camera;

  renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    powerPreference: "high-performance",
  });
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.setPixelRatio(getClampedRendererPixelRatio(initialSettings.quality));

  // Always use graphics settings from GraphicsSettingsManager
  const shadowsEnabled = initialSettings.shadowsEnabled;
  const shadowMapSize = initialSettings.shadowMapSize;

  // Override options with graphics settings to ensure consistency
  if (options) {
    options.shadowsEnabled = shadowsEnabled;
  }

  console.log("[Shadow Debug] Initializing renderer with shadows:", shadowsEnabled);
  renderer.shadowMap.enabled = shadowsEnabled; // shadowMap enabled/disabled based on setting
  renderer.shadowMap.type = getShadowMapType(initialSettings.quality);

  // Inicjalizacja raycaster i płaszczyzny ziemi
  raycaster = new THREE.Raycaster();
  groundNormal = new THREE.Vector3(0, 1, 0);
  groundPlane = new THREE.Plane(groundNormal);

  const ambientLight = new THREE.AmbientLight(0xffffff, 0.9); // Increase ambient from 0.8 to 0.9 for softer look
  scene.add(ambientLight);

  const directionalLight = new THREE.DirectionalLight(0xfffff5, 1.0); //0xffffeb for sun effect, 0xffffff neutral
  directionalLight.position.set(300, 100, 300);

  // Set castShadow based on the shadow setting
  console.log("[Shadow Debug] Setting up directional light shadows");
  directionalLight.castShadow = shadowsEnabled;
  directionalLight.shadow.mapSize.width = shadowMapSize;
  directionalLight.shadow.mapSize.height = shadowMapSize;
  directionalLight.shadow.camera.near = 10;
  directionalLight.shadow.camera.far = 350;
  directionalLight.shadow.camera.left = -SHADOW_FRUSTUM_HALF_EXTENT;
  directionalLight.shadow.camera.right = SHADOW_FRUSTUM_HALF_EXTENT;
  directionalLight.shadow.camera.top = SHADOW_FRUSTUM_HALF_EXTENT;
  directionalLight.shadow.camera.bottom = -SHADOW_FRUSTUM_HALF_EXTENT;
  directionalLight.shadow.bias = -0.0001; //reduce shadow acne
  scene.add(directionalLight);
  scene.add(directionalLight.target);
  shadowLight = directionalLight;
  console.log("[Shadow Debug] Directional light shadow properties:", {
    castShadow: directionalLight.castShadow,
    mapSize: directionalLight.shadow.mapSize,
    camera: {
      near: directionalLight.shadow.camera.near,
      far: directionalLight.shadow.camera.far,
    },
  });

  // Inicjalizacja socket.io przed utworzeniem czołgu
  const socket = setupSocket(null, scene); // Przekazujemy null jako playerTank, zaktualizujemy później
  window.socket = socket; // Ustaw socket globalnie przed utworzeniem czołgu

  // Inicjalizacja BotManager dla wszystkich graczy
  return new Promise<void>((resolve, reject) => {
    const cleanupSocketListeners = () => {
      socket.off("connect", onConnect);
      socket.off("connect_error", onConnectError);
      socket.off("error", onSocketError);
    };

    const onConnect = async () => {
      try {
        // Teraz tworzymy czołg, gdy socket jest już połączony
        // Get the selected tank model from options or use default
        const selectedTankModel = options?.selectedTankModel || "standard";
        console.log("[Game] Initializing game with options:", options);
        console.log("[Game] Creating tank with model:", selectedTankModel);

        // Create tank with the selected model
        tank = new Tank(scene, undefined, selectedTankModel);
        tank.mesh.position.set(0, 0.5, 0);
        scene.add(tank.mesh);

        // Set the tank's username if provided
        if (options?.username) {
          tank.setUsername(options.username);
        }

        // Initialize mobile controller if on mobile device
        const isMobile = options?.isMobile === true;
        console.log("[Game] Mobile device detected:", isMobile, "Screen width:", window.innerWidth);

        if (isMobile) {
          console.log("[Game] Initializing mobile controls");
          // Use the Tank's built-in method instead of creating a new controller directly
          tank.initMobileController();
          console.log("[Game] Mobile controls initialized");
        }

        window.playerTank = tank;
        socket.playerTank = tank;

        // Wyślij event inicjalizacji gracza
        socket.emit("playerInit", {
          playerId: tank.playerId,
          username: tank.username,
        });

        // Nasłuchuj na potwierdzenie inicjalizacji
        socket.once("playerInitialized", (data: { playerId: string }) => {
          if (data.playerId === tank.playerId) {
            console.log("Player initialized:", tank.playerId);
            tank.setInitialized(true);
          }
        });

        // Ustaw początkową pozycję kamery względem czołgu
        const initialCameraPosition = new THREE.Vector3(0, 5, -10);
        camera.position.copy(initialCameraPosition.add(tank.mesh.position));
        camera.lookAt(tank.mesh.position);

        // Wykonaj natychmiastowe renderowanie sceny
        renderer.render(scene, camera);
        tankVisibleTime = performance.now();

        // Dodaj zdarzenie tankDamage
        socket.on("tankDamage", (data) => {
          if (data.targetId === tank.playerId) {
            // Aktualizuj HP lokalnego czołgu
            if (window.updateHUDHealth) {
              window.updateHUDHealth(data.health);
            }
            if (window.playerTank) {
              const oldHealth = window.playerTank.health;
              window.playerTank.health = data.health;

              // Sprawdź czy czołg został zniszczony
              if (data.health <= 0 && !window.playerTank.isDestroyed) {
                window.playerTank.isDestroyed = true;
                // Pokaż napis "Wasted" przed wywołaniem destroy()
                showWastedOverlay();
                window.playerTank.destroy();
                // Zatrzymaj ruch czołgu
                window.playerTank.stopMovingNetwork();
                console.log("[Game] Player died, waiting for server respawn event...");
                // Serwer automatycznie wyśle tankRespawn po 5 sekundach
              }
            }
          } else {
            // Aktualizuj HP zdalnego czołgu
            if (window.remoteTanks) {
              const remoteTank = window.remoteTanks.find((tank) => tank.playerId === data.targetId);
              if (remoteTank) {
                const oldHealth = remoteTank.health;
                remoteTank.health = data.health;

                // Sprawdź czy zdalny czołg został zniszczony
                if (data.health <= 0 && oldHealth > 0) {
                  remoteTank.destroy();
                }
              } else {
                //console.log("Remote tank not found with targetId:", data.targetId);
              }
            }
          }
        });

        // Dodaj zdarzenie tankRespawn
        socket.on("tankRespawn", (data: { targetId: string; health: number }) => {
          console.log("[Game] Received tankRespawn event:", data);

          // Sprawdź czy gameState istnieje
          if (!window.gameState?.tanks) {
            console.error("[Game] gameState or tanks array is undefined!");
            return;
          }

          const tankToRespawn = window.gameState.tanks.find((t) => t.playerId === data.targetId);
          const isLocalPlayer = data.targetId === window.playerTank?.playerId;

          console.log("[Game] Tank to respawn:", {
            found: !!tankToRespawn,
            isLocalPlayer,
            targetId: data.targetId,
            localPlayerId: window.playerTank?.playerId,
          });

          if (tankToRespawn) {
            // Ustaw zdrowie przed respawnem
            tankToRespawn.health = data.health;

            // Wywołaj funkcję respawnu
            tankToRespawn.spawn();
            console.log("[Game] Tank respawned successfully:", data.targetId);

            // Aktualizuj HUD jeśli to lokalny czołg
            if (isLocalPlayer) {
              // Ukryj overlay "WASTED"
              hideWastedOverlay();

              if (window.updateHUDHealth) {
                window.updateHUDHealth(data.health);
              }
              console.log("[Game] Local player respawned, HUD updated");
            }
          } else {
            console.warn("[Game] Tank to respawn not found with targetId:", data.targetId);
          }
        });

        const environmentService = new EnvironmentService(socket);
        environment = createEnvironment(scene, camera, environmentService);

        // Ustaw środowisko dla czołgu i dodaj czołg do środowiska
        tank.setEnvironment(environment);
        environment.addTank(tank);

        // Dodaj czołg do globalnego stanu gry
        if (!window.gameState) {
          window.gameState = { tanks: [] };
        }
        window.gameState.tanks.push(tank);
        window.gameState.scene = scene;
        window.gameState.environmentService = environmentService;

        environmentLoadedTime = performance.now();

        // Inicjalizacja botów AI
        // botManager already initialized at the top level

        if (environment) {
          botManager.setEnvironment(environment);
          botManager.setCamera(camera); // Add camera reference for raycasting
          botManager.addBots(scene, environment);
        } else {
          //console.error("[Game] Cannot add bots - environment not initialized!");
        }

        // Inicjalizacja systemu tras treningowych
        trainingTrackManager = new TrainingTrackManager(scene);
        trainingTrackService = new TrainingTrackService(socket);

        // Dodaj referencje do globalnego stanu gry
        if (window.gameState) {
          window.gameState.trainingTrackManager = trainingTrackManager;
          window.gameState.trainingTrackService = trainingTrackService;
          window.gameState.Bot = Bot;
          window.gameState.botManager = botManager;
        }

        // Pobierz dostępne trasy treningowe z serwera
        //trainingTrackService.requestTracks();

        // Utwórz trasę treningową w pobliżu strefy respawnu
        //const trainingTrackSetup = new TrainingTrackSetup(trainingTrackManager, scene);
        //trainingTrackSetup.createTrackNearRespawnZone(tank.playerId);

        // Dodanie event listenerów
        //document.addEventListener("mousemove", onMouseMove);
        //document.addEventListener("mousedown", onMouseDown);
        //canvas.addEventListener("wheel", onMouseWheel);
        // canvas.addEventListener("click", lockMouse);
        //document.addEventListener("keydown", onKeyDown);
        //document.addEventListener("keyup", onKeyUp);
        // Dodanie event listenerów z EventManager
        EventManager.addListener("game", document, "mousemove", ((e: Event) =>
          onMouseMove(e as MouseEvent)) as EventListener);
        EventManager.addListener("game", document, "mousedown", ((e: Event) =>
          onMouseDown(e as MouseEvent)) as EventListener);
        EventManager.addListener("game", canvas, "wheel", ((e: Event) =>
          onMouseWheel(e as WheelEvent)) as EventListener);
        EventManager.addListener("game", canvas, "click", ((e: Event) => lockMouse(e as MouseEvent)) as EventListener);
        EventManager.addListener("game", document, "keydown", ((e: Event) =>
          onKeyDown(e as KeyboardEvent)) as EventListener);
        EventManager.addListener("game", document, "keyup", ((e: Event) =>
          onKeyUp(e as KeyboardEvent)) as EventListener);

        removeHUD();
        createHUD();

        window.updateHUDHealth = (health: number) => {
          if (window.playerTank) {
            window.playerTank.health = health;
            // Pass deltaTime as 0 for instant health updates since they don't need smoothing
            updateHUD(window.playerTank, fps, 0);
          } else {
            console.warn("DEBUG: playerTank not available for health update");
          }
        };

        // Inicjalizacja zmiennych czasowych
        lastFrameTime = performance.now();
        lastFpsUpdateTime = performance.now();
        frameCount = 0;

        const placeTankAtInitialRespawn = () => {
          const respawnPoint = environment.getRandomRespawnPoint();
          if (!respawnPoint) {
            return;
          }

          tank.mesh.position.copy(respawnPoint.position);
          tank.mesh.rotation.y = respawnPoint.rotation;
          camera.position.copy(new THREE.Vector3(0, 5, -10).add(tank.mesh.position));
          camera.lookAt(tank.mesh.position);

          socket.emit("tankPosition", {
            playerId: tank.playerId,
            position: tank.mesh.position.toArray(),
            rotation: tank.mesh.rotation.y,
          });
        };

        EventManager.addListener(
          "game",
          document,
          "environment:chunksReceived",
          placeTankAtInitialRespawn as EventListener,
          { once: true },
        );

        // Żądanie początkowych chunków
        environment.updateChunks(tank.mesh.position);

        animate();

        //window.addEventListener("resize", onWindowResize);
        EventManager.addListener("game", window, "resize", onWindowResize);

        // Dodaj obsługę blokady myszy
        //document.addEventListener("pointerlockchange", onPointerLockChange);
        EventManager.addListener("game", document, "pointerlockchange", onPointerLockChange);

        cleanupSocketListeners();
        resolve();
      } catch (error) {
        cleanupSocketListeners();
        reject(error as Error);
      }
    };

    const onConnectError = (error: Error) => {
      cleanupSocketListeners();
      reject(error);
    };

    const onSocketError = (error: Error) => {
      cleanupSocketListeners();
      reject(error);
    };

    socket.once("connect", onConnect);
    socket.once("connect_error", onConnectError);
    socket.once("error", onSocketError);
  });
}

function lockMouse(event: MouseEvent) {
  if (!isMouseLocked) {
    renderer.domElement.requestPointerLock();
  }
}

function onPointerLockChange() {
  isMouseLocked = document.pointerLockElement === renderer.domElement;

  // Jeśli myszka jest zablokowana, ukryj celownik systemowy
  if (isMouseLocked) {
    document.body.style.cursor = "none";
    setCrosshairVisibility(true);
  } else {
    document.body.style.cursor = "auto";
    setCrosshairVisibility(false);
  }
}

function onMouseMove(event: MouseEvent) {
  if (!isMouseLocked) return;

  // Przekaż ruch myszy do czołgu
  tank.handleMouseMove(event);

  // Aktualizuj pozycję celownika w świecie gry
  updateCrosshairPositionFromScreenCenter();

  // Aktualizuj celowanie wieży
  updateTurretAiming();
}

// Nowa funkcja do aktualizacji pozycji celownika na podstawie środka ekranu
function updateCrosshairPositionFromScreenCenter() {
  cameraDirection.set(0, 0, -1).applyQuaternion(camera.quaternion);
  cameraDirectionXZ.set(cameraDirection.x, 0, cameraDirection.z).normalize();
  window.crosshairPosition.copy(tank.mesh.position).addScaledVector(cameraDirectionXZ, 40);

  // Keep crosshair on ground level.
  window.crosshairPosition.y = 0;

  // Throttle logs to avoid spamming every frame.
  throttledLog("Crosshair position:", {
    x: window.crosshairPosition.x.toFixed(2),
    y: window.crosshairPosition.y.toFixed(2),
    z: window.crosshairPosition.z.toFixed(2),
    tankX: tank.mesh.position.x.toFixed(2),
    tankZ: tank.mesh.position.z.toFixed(2),
  });
}

function updateTurretAiming() {
  aimDirection.subVectors(window.crosshairPosition, tank.mesh.position);
  tankForward.set(0, 0, -1).applyQuaternion(tank.mesh.quaternion);
  horizontalAimDirection.set(aimDirection.x, 0, aimDirection.z).normalize();

  // Calculate horizontal aiming angle.
  const angle = Math.atan2(
    tankForward.x * horizontalAimDirection.z - tankForward.z * horizontalAimDirection.x,
    tankForward.x * horizontalAimDirection.x + tankForward.z * horizontalAimDirection.z,
  );

  // Set turret angle relative to tank body.
  //tank.setTurretRotation(angle)

  // Calculate vertical barrel angle.
  const horizontalDistance = Math.hypot(aimDirection.x, aimDirection.z);
  const verticalAngle = Math.atan2(aimDirection.y, horizontalDistance);
  //tank.setBarrelElevation(verticalAngle) //to nam resetuje polozenie lufy w kazdej klatce dlatego nie mozemy ruszac!

  // Throttle logs to avoid spamming every frame.
  throttledLog("Aiming:", {
    relativeAngle: ((angle * 180) / Math.PI).toFixed(2) + " deg",
    verticalAngle: ((verticalAngle * 180) / Math.PI).toFixed(2) + " deg",
    distance: horizontalDistance.toFixed(2),
  });
}

function onMouseDown(event: MouseEvent) {
  if (!isMouseLocked) return;

  // Aktualizuj pozycję celownika na podstawie kliknięcia
  updateCrosshairPositionFromScreenCenter();

  // Natychmiast zaktualizuj celowanie wieży
  updateTurretAiming();

  if (event.button === 0) {
    // lewy przycisk myszy
    // Przekaż pozycję celownika do metody shoot
    tank.shoot(scene);
  }
}

function onMouseWheel(event: WheelEvent) {
  cameraDistance = Math.max(
    MIN_CAMERA_DISTANCE,
    Math.min(MAX_CAMERA_DISTANCE, cameraDistance + event.deltaY * zoomSpeed * cameraDistance),
  );
}

function onKeyDown(event: KeyboardEvent) {
  // Sprawdź, czy czołg istnieje i nie jest zniszczony
  if (!tank || tank.isDestroyed) {
    return;
  }

  // Przekaż zdarzenie do czołgu
  tank.handleKeyDown(event);

  // Logujemy tylko gdy klawisz W lub S jest wciśnięty
  if (event.key === "w" || event.key === "ArrowUp" || event.key === "s" || event.key === "ArrowDown") {
  }

  if (event.key === "w") {
    tank.startMovingForward();
  }
  if (event.key === "s") {
    tank.startMovingBackward();
  }
  if (event.key === "a" || event.key === "ArrowLeft") {
    tank.startTurningLeft();
  }
  if (event.key === "d" || event.key === "ArrowRight") {
    tank.startTurningRight();
  }
  if (event.key === " ") {
    tank.shoot(scene);
  }
  if (event.key === "Shift") {
    tank.startHandbrake();
  }
}

function onKeyUp(event: KeyboardEvent) {
  // Sprawdź, czy czołg istnieje i nie jest zniszczony
  if (!tank || tank.isDestroyed) {
    return;
  }

  // Przekaż zdarzenie do czołgu
  tank.handleKeyUp(event);

  if (event.key === "w") {
    tank.stopMovingForward();
  }
  if (event.key === "s") {
    tank.stopMovingBackward();
  }
  if (event.key === "a" || event.key === "ArrowLeft") {
    tank.stopTurningLeft();
  }
  if (event.key === "d" || event.key === "ArrowRight") {
    tank.stopTurningRight();
  }
  if (event.key === "Shift") {
    tank.stopHandbrake();
  }
}

function animate() {
  //debug
  /*
  if (renderer) {
    memoryStats.runtime = (Date.now() - memoryStats.startTime) / 1000;
    memoryStats.geometries = renderer.info.memory.geometries;
    memoryStats.textures = renderer.info.memory.textures;
    memoryStats.objects = scene.children.length;
    memoryStats.triangles = renderer.info.render.triangles;
    memoryStats.calls = renderer.info.render.calls;
    memoryStats.frames = renderer.info.render.frame;

    if (memoryStats.runtime % 10 < 0.1) {
      // co ok. 10 sekund
      console.table(memoryStats);
    }
  }
    */
  //debug

  requestAnimationFrame(animate);

  const currentTime = performance.now();
  const deltaTime = (currentTime - lastFrameTime) / 1000;
  lastFrameTime = currentTime;

  // Log unusual frame times only.
  if (deltaTime > 0.3) {
    console.log("Unusual deltaTime:", deltaTime);
  }

  // If environment is not ready yet, render only the tank scene.
  if (!environment) {
    // Environment is still loading.
    renderer.render(scene, camera);
    return;
  }

  tank.update(deltaTime);

  // Keep the tight shadow frustum centered on the player.
  updateShadowFollow();

  // Update environment systems.
  environment.update();

  // Aktualizuj boty AI
  if (botManager) {
    botUpdateTanks.length = 0;
    if (window.gameState && window.gameState.tanks) {
      botUpdateTanks.push(...window.gameState.tanks);
    } else if (tank) {
      botUpdateTanks.push(tank);
    }
    botManager.update(deltaTime, botUpdateTanks);
  }

  // Aktualizuj system tras treningowych
  if (trainingTrackManager) {
    // Pass player position to training manager.
    trainingTrackManager.updatePlayerPosition(tank.mesh.position, tank);

    // Aktualizuj elementy trasy
    trainingTrackManager.update(deltaTime);
  }

  // Update crosshair and aiming every frame.
  updateCrosshairPositionFromScreenCenter();
  updateTurretAiming();

  // Camera follows tank direction, not turret direction.
  tankDirection.set(0, 0, -1).applyQuaternion(tank.mesh.quaternion);
  cameraOffset.copy(tankDirection).multiplyScalar(cameraDistance);
  cameraOffset.y += cameraDistance * 0.5;
  targetCameraPos.copy(tank.mesh.position).add(cameraOffset);
  camera.position.lerp(targetCameraPos, 0.1);

  // Look halfway between tank and crosshair.
  lookAtPoint.copy(tank.mesh.position).lerp(window.crosshairPosition, 0.5);
  camera.lookAt(lookAtPoint);

  // Adapt FOV based on speed.
  const baseFOV = 75;
  const speedFOV = baseFOV + (Math.abs(tank.movementSystem.currentSpeed) / tank.maxSpeed) * 15;
  const nextFov = THREE.MathUtils.lerp(camera.fov, speedFOV, 0.1);
  // Perf: updateProjectionMatrix rebuilds the projection each call - 0.05deg
  // steps are imperceptible but cut matrix updates ~5x vs 0.01.
  if (Math.abs(nextFov - lastAppliedCameraFov) > 0.05) {
    camera.fov = nextFov;
    camera.updateProjectionMatrix();
    lastAppliedCameraFov = nextFov;
  }

  // Check whether tank is in mud.
  if (isInMud(tank.mesh.position, environment.mudPatches)) {
    // Movement speed modifiers are handled in the movement controller.
  }

  checkTankMovement();

  // Aktualizuj HUD
  frameCount++;
  if (currentTime - lastFpsUpdateTime >= 1000) {
    fps = Math.round(frameCount / ((currentTime - lastFpsUpdateTime) / 1000));
    lastFpsUpdateTime = currentTime;
    frameCount = 0;
  }
  updateHUD(tank, fps, deltaTime);

  // Refresh nearby chunks at intervals while moving.
  if (isMoving && currentTime - lastChunkUpdateTime > ENVIRONMENT_CONFIG.CHUNK_UPDATE_INTERVAL) {
    environment.updateChunks(tank.mesh.position);
    lastChunkUpdateTime = currentTime;
  }

  renderer.render(scene, camera);
}

function onWindowResize() {
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(window.innerWidth, window.innerHeight);
  const settings = GraphicsSettingsManager.getInstance().getCurrentSettings();
  renderer.setPixelRatio(getClampedRendererPixelRatio(settings.quality));

  // Update mobile controller if tank exists
  if (tank && tank.mobileController) {
    tank.mobileController.resize();
  }
}

// call it from console
function inspectSceneGeometry() {
  const meshes: any[] = [];

  scene.traverse((object) => {
    if (object instanceof THREE.Mesh) {
      const geometry = object.geometry;
      let triangleCount = 0;

      if (geometry instanceof THREE.BufferGeometry) {
        const index = geometry.index;
        if (index) {
          triangleCount = index.count / 3;
        } else if (geometry.attributes.position) {
          triangleCount = geometry.attributes.position.count / 3;
        }
      }

      meshes.push({
        name: object.name || "unnamed",
        triangles: triangleCount,
        type: object.constructor.name,
        position: object.position.toArray().map((v) => Math.round(v * 100) / 100),
      });
    }
  });

  // Sortuj według liczby trójkątów (od największej)
  meshes.sort((a, b) => b.triangles - a.triangles);

  // Wyświetl Top 15 obiektów zużywających najwięcej trójkątów
  console.table(meshes.slice(0, 15));

  return meshes;
}

window.inspectSceneGeometry = inspectSceneGeometry;

window.analyzeScene = function () {
  // Dodajemy prawidłowe deklaracje typów
  const typeCount: { [key: string]: number } = {};
  const geometryStats: {
    [key: string]: {
      count: number;
      triangles: number;
      instances: number;
    };
  } = {};

  scene.traverse((object) => {
    // Reszta kodu bez zmian
    const typeName = object.constructor.name;
    typeCount[typeName] = (typeCount[typeName] || 0) + 1;

    if (object instanceof THREE.Mesh || object instanceof THREE.InstancedMesh) {
      const geo = object.geometry;
      const triCount = geo.attributes.position ? geo.attributes.position.count / 3 : 0;
      const name = object.name || "unnamed";

      geometryStats[name] = geometryStats[name] || {
        count: 0,
        triangles: 0,
        instances: 0,
      };
      geometryStats[name].count++;
      geometryStats[name].triangles += triCount;

      if (object instanceof THREE.InstancedMesh) {
        geometryStats[name].instances += object.count;
        console.log(`InstancedMesh: ${name}, count: ${object.count}, triangles: ${triCount}`);
      }
    }
  });

  console.log("Typy obiektów:", typeCount);
  console.log("Statystyki geometrii:", geometryStats);

  return { typeCount, geometryStats };
};
